import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { FciService } from '../../../shared/services/apis/motor-fiscal/fci.service';
import { ItemFciDialogComponent, ItemFciDialogData } from './dialogs/item-fci-dialog/item-fci-dialog.component';
import { FciItemRequest } from '@shared/services/requests/motor-fiscal/fci-item.request';

@Component({
  selector: 'app-fci-cadastro',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    AlertCardComponent,
    EmptyStateComponent,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatTableModule,
  ],
  templateUrl: './fci-cadastro.component.html',
  styleUrl: './fci-cadastro.component.css',
})
export class FciCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_Fci;

  public isLoading = false;
  public isSaving = false;
  public idFci: number | null = null;
  public dataCriacao: Date | null = null;

  public protocoloRecepcao: string | null = null;
  public itens: FciItemRequest[] = [];

  public get editando(): boolean {
    return this.idFci != null;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private alertService: AlertService,
    private fciService: FciService,
  ) { }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.idFci = Number(idParam);
      this.carregar(this.idFci);
    } else {
      this.dataCriacao = new Date();
    }
  }

  private carregar(idFci: number): void {
    this.isLoading = true;
    this.fciService.getById(idFci).subscribe({
      next: resp => {
        this.isLoading = false;
        if (resp.isError || !resp.fci) {
          this.alertService.show('Erro', resp.errorDescription || 'FCI não encontrada.', AlertType.error);
          this.voltar();
          return;
        }

        this.protocoloRecepcao = resp.fci.protocoloRecepcao;
        this.dataCriacao = new Date(resp.fci.datetimeInclusao);
        this.itens = resp.fci.itens.map(i => ({
          idProduto: i.idProduto,
          descricao: i.descricao,
          codigoSku: i.codigoSku,
          ncm: i.ncm,
          unidade: i.unidade,
          gtin: i.gtin,
          valorSaida: i.valorSaida,
          valorParcela: i.valorParcela,
          percentualConteudoImportacao: i.percentualConteudoImportacao,
          codigoFci: i.codigoFci,
        }));
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar a FCI.', AlertType.error);
        this.voltar();
      },
    });
  }

  public voltar(): void {
    this.router.navigate(['/suprimentos/fci']);
  }

  public numeroFormatado(idFci: number): string {
    return String(idFci);
  }

  public abrirItem(item: FciItemRequest | null, index: number | null): void {
    const dialogRef = this.dialog.open(ItemFciDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: { item } as ItemFciDialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirmed) return;

      if (index != null) {
        this.itens[index] = result.item;
        this.itens = [...this.itens];
      } else {
        this.itens = [...this.itens, result.item];
      }
    });
  }

  public excluirItem(index: number): void {
    this.itens = this.itens.filter((_, i) => i !== index);
  }

  public salvar(): void {
    if (this.itens.length === 0) {
      this.alertService.show('Atenção', 'Adicione ao menos uma mercadoria antes de salvar a FCI.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request = { protocoloRecepcao: this.protocoloRecepcao, itens: this.itens };

    const save$ = this.editando
      ? this.fciService.atualizar(this.idFci!, request)
      : this.fciService.criar(request);

    save$.subscribe({
      next: resp => {
        this.isSaving = false;
        if (resp.isError) {
          this.alertService.show('Aviso', resp.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', this.editando ? 'FCI atualizada.' : 'Nova FCI cadastrada com sucesso.', AlertType.success);
        this.voltar();
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }
}
